import { RepeatIcon } from '@chakra-ui/icons';
import { Box, HStack, IconButton, Skeleton, VStack, Wrap, WrapItem } from '@chakra-ui/react';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useEffect, useState } from 'react';

import Icon from 'components/Icon';
import Text from 'components/Text';

import DevSandboxTooltip from '../../DevSandboxTooltip';
import { ColorPalette, generateColorPalette } from '../../utils';

interface IDesignSystemTabProps {
  designScreenshotUrl?: string | null;
}

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  return { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) };
};

const hexToHsl = (hex: string) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

const getLuminance = (hex: string) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
};

const getSaturation = (hex: string) => {
  const hsl = hexToHsl(hex);
  return hsl ? hsl.s : 0;
};

const categorizeColors = (palette: string[]) => {
  const sorted = [...palette].sort((a, b) => getLuminance(b) - getLuminance(a));

  const background = sorted.filter((c) => getLuminance(c) > 0.7);
  const text = sorted.filter((c) => getLuminance(c) < 0.3);
  const accent = sorted.filter((c) => getSaturation(c) > 50 && getLuminance(c) >= 0.3 && getLuminance(c) <= 0.7);
  const neutral = sorted.filter(
    (c) => getSaturation(c) <= 50 && getLuminance(c) >= 0.3 && getLuminance(c) <= 0.7
  );

  return {
    background: background.length > 0 ? background : [sorted[0] || '#ffffff'],
    text: text.length > 0 ? text : [sorted[sorted.length - 1] || '#333333'],
    primary: accent.length > 0 ? [accent[0]] : [sorted[Math.floor(sorted.length / 2)] || '#4a90d9'],
    accent: accent.length > 1 ? accent.slice(1) : [],
    neutral: neutral,
  };
};

const SPACING_SCALE = [
  { token: '--space-1', value: '4px', desc: 'Tight' },
  { token: '--space-2', value: '8px', desc: 'Small' },
  { token: '--space-3', value: '12px', desc: 'Compact' },
  { token: '--space-4', value: '16px', desc: 'Default' },
  { token: '--space-5', value: '24px', desc: 'Comfortable' },
  { token: '--space-6', value: '32px', desc: 'Spacious' },
  { token: '--space-8', value: '48px', desc: 'Large' },
  { token: '--space-10', value: '64px', desc: 'Extra large' },
];

const FONT_SCALE = [
  { token: '--font-xs', value: '12px', desc: 'Caption' },
  { token: '--font-sm', value: '14px', desc: 'Small text' },
  { token: '--font-base', value: '16px', desc: 'Body' },
  { token: '--font-lg', value: '18px', desc: 'Large body' },
  { token: '--font-xl', value: '20px', desc: 'Small heading' },
  { token: '--font-2xl', value: '24px', desc: 'Heading' },
  { token: '--font-3xl', value: '30px', desc: 'Large heading' },
  { token: '--font-4xl', value: '36px', desc: 'Display' },
];

const DesignSystemTab = ({ designScreenshotUrl }: IDesignSystemTabProps) => {
  const [colorPalette, setColorPalette] = useState<ColorPalette | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copyFormat, setCopyFormat] = useState<'hex' | 'rgb' | 'hsl'>('hex');
  const { toastSuccess } = useToastWrapper();

  const loadColorPalette = useCallback(async () => {
    setIsLoading(true);
    try {
      const palette = await generateColorPalette(designScreenshotUrl);
      setColorPalette(palette);
    } catch {
      const palette = await generateColorPalette(null);
      setColorPalette(palette);
    } finally {
      setIsLoading(false);
    }
  }, [designScreenshotUrl]);

  useEffect(() => {
    loadColorPalette();
  }, [loadColorPalette]);

  const formatColor = (hex: string) => {
    switch (copyFormat) {
      case 'rgb': {
        const rgb = hexToRgb(hex);
        return rgb ? `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` : hex;
      }
      case 'hsl': {
        const hsl = hexToHsl(hex);
        return hsl ? `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` : hex;
      }
      default:
        return hex.toUpperCase();
    }
  };

  const handleCopyColor = (hex: string) => {
    const formatted = formatColor(hex);
    navigator.clipboard.writeText(formatted);
    toastSuccess(`Copied ${formatted}`);
  };

  const categorized = colorPalette ? categorizeColors(colorPalette.palette) : null;

  const generateCSSTokens = () => {
    if (!categorized) return '';

    const lines: string[] = [':root {'];

    // Colors
    if (categorized.background[0]) lines.push(`  --color-bg: ${formatColor(categorized.background[0])};`);
    if (categorized.text[0]) lines.push(`  --color-text: ${formatColor(categorized.text[0])};`);
    if (categorized.primary[0]) lines.push(`  --color-primary: ${formatColor(categorized.primary[0])};`);
    categorized.accent.forEach((c, i) => {
      lines.push(`  --color-accent${categorized.accent.length > 1 ? `-${i + 1}` : ''}: ${formatColor(c)};`);
    });

    lines.push('');

    // Typography
    FONT_SCALE.forEach((f) => {
      lines.push(`  ${f.token}: ${f.value};`);
    });

    lines.push('');

    // Spacing
    SPACING_SCALE.forEach((s) => {
      lines.push(`  ${s.token}: ${s.value};`);
    });

    lines.push('}');
    return lines.join('\n');
  };

  const handleCopyAllTokens = () => {
    const tokens = generateCSSTokens();
    navigator.clipboard.writeText(tokens);
    toastSuccess('CSS design tokens copied!');
  };

  const renderColorCategory = (label: string, colors: string[]) => {
    if (colors.length === 0) return null;

    return (
      <Box mb={3}>
        <Text fontSize="micro" opacity="secondary" mb={1} textTransform="uppercase" letterSpacing="wide">
          {label}
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          {colors.map((color, i) => (
            <Box
              key={`${label}-${i}`}
              cursor="pointer"
              onClick={() => handleCopyColor(color)}
              _hover={{ transform: 'scale(1.1)' }}
              transition="transform 0.15s"
            >
              <Box
                width="48px"
                height="48px"
                bg={color}
                borderRadius="md"
                borderWidth="1px"
                borderColor="whiteAlpha.200"
              />
              <Text fontSize="micro" textAlign="center" mt={1} fontFamily="mono">
                {formatColor(color)}
              </Text>
            </Box>
          ))}
        </HStack>
      </Box>
    );
  };

  return (
    <Box>
      {/* Info box */}
      <Box mb={4} p={3} bg="purple.900" borderRadius="md" borderWidth="1px" borderColor="purple.600">
        <Text fontSize="small" fontWeight="semibold" mb={1}>
          Figma Design System
        </Text>
        <Text fontSize="micro" opacity="secondary">
          Detailed design tokens from Figma (exact colors, typography, spacing) will be available in a
          future update. Download the Figma file from the Design Assets tab for now.
        </Text>
      </Box>

      {/* Color System */}
      <Box mb={6}>
        <HStack spacing={2} mb={2}>
          <Text fontSize="large" fontWeight="semibold">
            Color System
          </Text>
          <DevSandboxTooltip label="Copy all as CSS tokens">
            <IconButton
              aria-label="Copy CSS tokens"
              icon={<Icon type="Code" size={16} />}
              size="sm"
              variant="ghost"
              onClick={handleCopyAllTokens}
              isDisabled={isLoading || !colorPalette}
            />
          </DevSandboxTooltip>
          <DevSandboxTooltip label={designScreenshotUrl ? 'Re-extract colors' : 'Generate new palette'}>
            <IconButton
              aria-label="Refresh palette"
              icon={<RepeatIcon />}
              size="sm"
              variant="ghost"
              onClick={loadColorPalette}
              isLoading={isLoading}
            />
          </DevSandboxTooltip>
        </HStack>
        <Text fontSize="small" opacity="secondary" mb={3}>
          {designScreenshotUrl
            ? 'Colors extracted from the design screenshot, categorized by role.'
            : 'Random color palette. Open a project or challenge for design-specific colors.'}
        </Text>

        {/* Format toggle */}
        <Wrap spacing={2} mb={4}>
          <WrapItem>
            <Text fontSize="small" opacity="secondary" lineHeight="28px">
              Format:
            </Text>
          </WrapItem>
          {(['hex', 'rgb', 'hsl'] as const).map((format) => (
            <WrapItem key={format}>
              <Box
                px={3}
                py={1}
                borderRadius="full"
                fontSize="small"
                cursor="pointer"
                bg={copyFormat === format ? 'purple.500' : 'whiteAlpha.200'}
                _hover={{ bg: copyFormat === format ? 'purple.400' : 'whiteAlpha.300' }}
                transition="all 0.15s"
                onClick={() => setCopyFormat(format)}
                textTransform="uppercase"
              >
                {format}
              </Box>
            </WrapItem>
          ))}
        </Wrap>

        {/* Color swatches by category */}
        {isLoading ? (
          <VStack spacing={3} alignItems="stretch">
            {Array.from({ length: 3 }).map((_, i) => (
              <Box key={i}>
                <Skeleton height="12px" width="80px" mb={2} />
                <HStack spacing={2}>
                  {Array.from({ length: 3 }).map((__, j) => (
                    <Skeleton key={j} width="48px" height="48px" borderRadius="md" />
                  ))}
                </HStack>
              </Box>
            ))}
          </VStack>
        ) : categorized ? (
          <>
            {renderColorCategory('Background', categorized.background)}
            {renderColorCategory('Text', categorized.text)}
            {renderColorCategory('Primary', categorized.primary)}
            {renderColorCategory('Accent', categorized.accent)}
            {renderColorCategory('Neutral', categorized.neutral)}
          </>
        ) : null}
      </Box>

      {/* Typography System */}
      <Box mb={6}>
        <Text fontSize="large" fontWeight="semibold" mb={2}>
          Typography
        </Text>
        <Text fontSize="small" opacity="secondary" mb={3}>
          Recommended font size scale for consistent hierarchy.
        </Text>
        <VStack spacing={1} alignItems="stretch">
          {FONT_SCALE.map((f) => (
            <HStack
              key={f.token}
              justifyContent="space-between"
              p={2}
              bg="whiteAlpha.50"
              borderRadius="sm"
              cursor="pointer"
              _hover={{ bg: 'whiteAlpha.100' }}
              onClick={() => {
                navigator.clipboard.writeText(`${f.token}: ${f.value};`);
                toastSuccess(`Copied ${f.token}: ${f.value}`);
              }}
            >
              <HStack spacing={3}>
                <Text fontSize="micro" fontFamily="mono" color="purple.300" minW="90px">
                  {f.token}
                </Text>
                <Text fontSize="micro" opacity="secondary">
                  {f.desc}
                </Text>
              </HStack>
              <Text fontSize="micro" fontFamily="mono">
                {f.value}
              </Text>
            </HStack>
          ))}
        </VStack>
      </Box>

      {/* Spacing System */}
      <Box mb={6}>
        <Text fontSize="large" fontWeight="semibold" mb={2}>
          Spacing
        </Text>
        <Text fontSize="small" opacity="secondary" mb={3}>
          Consistent spacing scale for layout and component spacing.
        </Text>
        <VStack spacing={1} alignItems="stretch">
          {SPACING_SCALE.map((s) => (
            <HStack
              key={s.token}
              justifyContent="space-between"
              p={2}
              bg="whiteAlpha.50"
              borderRadius="sm"
              cursor="pointer"
              _hover={{ bg: 'whiteAlpha.100' }}
              onClick={() => {
                navigator.clipboard.writeText(`${s.token}: ${s.value};`);
                toastSuccess(`Copied ${s.token}: ${s.value}`);
              }}
            >
              <HStack spacing={3}>
                <Text fontSize="micro" fontFamily="mono" color="purple.300" minW="90px">
                  {s.token}
                </Text>
                <Text fontSize="micro" opacity="secondary">
                  {s.desc}
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Box
                  width={s.value}
                  height="8px"
                  bg="purple.400"
                  borderRadius="sm"
                  opacity={0.6}
                />
                <Text fontSize="micro" fontFamily="mono" minW="32px" textAlign="right">
                  {s.value}
                </Text>
              </HStack>
            </HStack>
          ))}
        </VStack>
      </Box>

      {/* CSS Design Tokens */}
      <Box>
        <HStack spacing={2} mb={2}>
          <Text fontSize="large" fontWeight="semibold">
            CSS Design Tokens
          </Text>
          <DevSandboxTooltip label="Copy all tokens">
            <IconButton
              aria-label="Copy all tokens"
              icon={<Icon type="Copy" size={14} />}
              size="sm"
              variant="ghost"
              onClick={handleCopyAllTokens}
              isDisabled={isLoading}
            />
          </DevSandboxTooltip>
        </HStack>
        <Text fontSize="small" opacity="secondary" mb={3}>
          Complete CSS custom properties ready to paste into your stylesheet.
        </Text>
        <Box
          p={3}
          bg="blackAlpha.400"
          borderRadius="md"
          fontFamily="mono"
          fontSize="micro"
          whiteSpace="pre-wrap"
          maxH="300px"
          overflowY="auto"
          cursor="pointer"
          _hover={{ bg: 'blackAlpha.500' }}
          onClick={handleCopyAllTokens}
        >
          {isLoading ? 'Loading...' : generateCSSTokens()}
        </Box>
      </Box>

    </Box>
  );
};

export default DesignSystemTab;
